import {
  SetDataOptions,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { keyBy } from 'lodash';
import { useDispatch } from 'react-redux';
import {
  Account,
  AccountAPI,
  AccountConfig,
  AccountInitDataProjectsQuery,
  AccountInitialData,
  AccountSignUpPayload,
  AccountSignUpResponse,
  AccountSignUpThroughGooglePayload,
  CalendarConfigPayload,
  Currency,
  CurrencyList,
  CurrencyMapping,
  CurrencyMappingList,
  IsDomainExistsPayload,
  IsDomainExistsResponse,
  NotificationData,
  UpdateAccountTimezonePayload,
  UpdateCurrencyMappingPayload,
} from 'api/account';
import {
  AuditLog,
  AuditLogMetaName,
  CurrencyAuditLogMeta,
} from 'api/financials-audit-logs/type';
import { Company, UserId } from 'api/models';
import { UserAPI, UserMetaData } from 'api/user';
import { GENERAL_API_STALE } from 'config/query-config';
import { LS_TEMPLATE_ID_KEY, RoutePaths } from 'constant';
import { COGNITO_ENABLED_KEY } from 'constant/constant';
import { set } from 'data/account';
import { success } from 'data/current-user-profile';
import { personalTasksSlice } from 'data/personal-tasks/slice';
import { putAll as putAllCompanies } from 'stores/companies';
import { putAll as putAllPermissionSets } from 'stores/permission-sets';
import { update as updateVisibilitySettings } from 'stores/visibility';
import { currentUserState } from 'utils/current-user-state';
import { isLocalhost } from 'utils/is-localhost';
import { applyPastelTheme } from '../utils/pastel-color';
import { getQueryAdvancedSettingsConfigKey } from './advanced-settings/advanced-settings';
import { oneDay } from './constants';
import { HOLIDAY_CALENDAR_KEY } from './holiday-calendar';

export const ACCOUNT_META_DATA_QUERY_KEY = ['account-meta-data'];
export const NOTIFICATION_DATA_QUERY_KEY = ['notification-data'];
export const G2_QUERY_KEY = ['g2-data'];
export const INIT_PROJECTS_QUERY_KEY = ['init-projects'];
export const CURRENCY_MAPPING_LIST_KEY = ['CURRENCY_MAPPING_LIST'];
export type CurrencyMappingHistoryKey = ['CURRENCY_MAPPING_HISTORY', string];
export type CurrencyMappingAuditLogKey = ['CURRENCY_MAPPING_AUDIT_LOG', string];

const getCurrencyMappingHistoryKey = (
  currencyCode: Currency['currencyCode'],
): CurrencyMappingHistoryKey => ['CURRENCY_MAPPING_HISTORY', currencyCode];

const getCustomCalendarConfigKey = () => ['CUSTOM_CALENDAR_CONFIG'];

const getCurrencyMappingAuditLogKey = (
  currencyCode: Currency['currencyCode'],
): CurrencyMappingAuditLogKey => ['CURRENCY_MAPPING_AUDIT_LOG', currencyCode];

const commonOptions: Pick<
  UseQueryOptions<AccountInitialData>,
  'queryKey' | 'queryFn' | 'staleTime' | 'refetchOnWindowFocus'
> = {
  queryKey: ACCOUNT_META_DATA_QUERY_KEY,
  queryFn: () => AccountAPI.getInitData(),
  staleTime: oneDay,
  refetchOnWindowFocus: false,
};

// This is a custom hook that will return the notification data via set timeout
// Based on this initial data we can update the pending notifications count in query client cache via websocket
export const useNotificationDataQuery =
  (): UseQueryResult<NotificationData> => {
    return useQuery({
      queryKey: NOTIFICATION_DATA_QUERY_KEY,
      // promise with timeout
      queryFn: () => {
        return new Promise((resolve) => {
          resolve({
            pendingNotifications: 0,
            newNotificationAvailable: false,
          });
        });
      },
    });
  };

export const useAccountDataQuery = (): UseQueryResult<AccountInitialData> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useQuery({
    ...commonOptions,
    queryFn: () =>
      AccountAPI.getInitData().then((response) => {
        const {
          account,
          accountMeta,
          accountConfig,
          userProfile,
          companies,
          permissionSetList,
          accountVisibilitySettings,
          personalTasks,
          holidayCalendar,
          newNotificationAvailable,
          showG2,
        } = response;

        queryClient.setQueryData<boolean>(G2_QUERY_KEY, showG2);

        if (accountConfig) {
          queryClient.setQueryData(
            getQueryAdvancedSettingsConfigKey(),
            accountConfig,
          );
        }
        queryClient.setQueryData(NOTIFICATION_DATA_QUERY_KEY, {
          // increment the pending notifications count by 1 when a new notification is received.
          pendingNotifications: newNotificationAvailable ? 1 : 0,
          // set the newNotificationAvailable property to false after the first render
          newNotificationAvailable,
        });
        const holidayCalendarCacheData =
          queryClient.getQueryData(HOLIDAY_CALENDAR_KEY);

        if (holidayCalendar && !holidayCalendarCacheData) {
          queryClient.setQueryData(
            HOLIDAY_CALENDAR_KEY,
            keyBy(holidayCalendar, 'holidayCalendarId'),
            {
              staleTime: GENERAL_API_STALE,
            } as SetDataOptions,
          );
        }
        if (userProfile.projects != null) {
          queryClient.setQueryData(INIT_PROJECTS_QUERY_KEY, () => {
            return {
              projects: userProfile.projects,
              byId: keyBy(userProfile.projects, 'projectId'),
            };
          });
        }
        if (account.metaData.theme?.sideNavBg) {
          applyPastelTheme(account.metaData.theme?.sideNavBg);
        }
        dispatch(set(account));
        dispatch(success(userProfile));
        dispatch(putAllCompanies(companies ?? []));
        dispatch(putAllPermissionSets(permissionSetList));
        dispatch(personalTasksSlice.actions.fetchSuccess(personalTasks));
        if (accountVisibilitySettings) {
          dispatch(updateVisibilitySettings(accountVisibilitySettings));
        }
        currentUserState.userState = {
          currentUser: userProfile,
          vendorCompany: accountMeta.company,
        };

        // save in local storage for future use to avoid unnecessary api calls
        // since accountMeta is not going to change frequently
        // accountMeta will be cleared on logout
        // isLocalhost check id for dev purpose. Otherwise domain name has to be changed from inside the localstorage
        if (!isLocalhost()) {
          localStorage.setItem('accountMeta', JSON.stringify(accountMeta));
        }

        return response;
      }),
  });
};

export const ACCOUNT_CONFIG_QUERY_KEY = ['account-config'];

export const useAccountConfigQuery = (): UseQueryResult<AccountConfig> => {
  return useQuery({
    queryKey: ACCOUNT_CONFIG_QUERY_KEY,
    queryFn: () => AccountAPI.getConfigData(),
  });
};

export const useProjectsFromInitData =
  (): UseQueryResult<AccountInitDataProjectsQuery> => {
    const { data: initData, isLoading } = useAccountDataQuery();

    return useQuery(
      INIT_PROJECTS_QUERY_KEY,
      () => {
        const projectPermissionBeanMap =
          initData?.userProfile?.permissions?.projectPermissionBeanMap;

        return {
          projects: initData?.userProfile.projects.map((project) => ({
            ...project,
            permissionSet: project?.projectId
              ? projectPermissionBeanMap?.[project?.projectId?.toString()]
                  ?.permissionSet
              : undefined,
          })),
          byId: keyBy(initData?.userProfile.projects, 'projectId'),
          isLoading,
          projectPermissionBeanMap,
        };
      },
      {
        enabled: Boolean(initData),
      },
    );
  };

export const useAllCompanies = (): UseQueryResult<Company[], unknown> => {
  return useQuery<AccountInitialData, unknown, Company[]>({
    ...commonOptions,
    select: (data) => data.companies,
  });
};

export const useAccountSignUpMutation = (
  cookiesEnabled?: boolean,
): UseMutationResult<AccountSignUpResponse, unknown, AccountSignUpPayload> => {
  const isCognitoEnabled = localStorage.getItem(COGNITO_ENABLED_KEY) === 'true';

  localStorage.removeItem(COGNITO_ENABLED_KEY);

  return useMutation({
    mutationFn: (payload) => AccountAPI.signUp(payload),
    onSuccess: (res) => {
      // Cookies flow: auth via rl_session cookie — redirect to /login to trigger OAuth.
      if (cookiesEnabled) {
        window.location.replace(
          `https://${res.account.primaryDomainName}/login`,
        );

        return;
      }

      if (isCognitoEnabled) {
        if (res.templateId) {
          localStorage.setItem(LS_TEMPLATE_ID_KEY, res.templateId.toString());
        }
        window.location.replace(
          `https://${`${res.account.primaryDomainName}/login`}?token=${
            res.session.sessionToken
          }&cognito=${true}`,
        );

        return;
      }

      if (res.templateId) {
        window.location.replace(
          `https://${
            res.account.primaryDomainName + RoutePaths.loginWithSession
          }?token=${res.session.sessionToken}&templateId=${res.templateId}`,
        );

        return;
      }
      window.location.replace(
        `https://${
          res.account.primaryDomainName + RoutePaths.loginWithSession
        }?token=${res.session.sessionToken}`,
      );
    },
  });
};

export const useSignUpThroughGoogle = (): UseMutationResult<
  AccountSignUpResponse,
  unknown,
  AccountSignUpThroughGooglePayload,
  unknown
> => {
  return useMutation({
    mutationFn: (payload: AccountSignUpThroughGooglePayload) =>
      AccountAPI.signUpThroughGoogle(payload),
    onSuccess: (res) => {
      if (res.templateId) {
        window.location.replace(
          `https://${
            res.account.primaryDomainName + RoutePaths.loginWithSession
          }?token=${res.session.sessionToken}&templateId=${res.templateId}`,
        );

        return;
      }
      window.location.replace(
        `https://${
          res.account.primaryDomainName + RoutePaths.loginWithSession
        }?token=${res.session.sessionToken}`,
      );
    },
  });
};

export const useIsDomainExistsMutation = (): UseMutationResult<
  IsDomainExistsResponse,
  unknown,
  IsDomainExistsPayload
> => {
  return useMutation({
    mutationFn: (payload) => AccountAPI.isDomainExists(payload),
  });
};

export const ACCOUNT_PROFILE_KEY = ['ACC_PROFILE'];

export const useAccountProfile = (
  queryOptions: UseQueryOptions<
    Account,
    unknown,
    Account,
    typeof ACCOUNT_PROFILE_KEY
  > = {},
): UseQueryResult<Account> =>
  useQuery(ACCOUNT_PROFILE_KEY, () => AccountAPI.getAccountDetails(), {
    staleTime: GENERAL_API_STALE,
    ...queryOptions,
  });

const updateAccTimezoneFn = (data: UpdateAccountTimezonePayload) => {
  return AccountAPI.updateTimezone(data);
};

export const useAccountTimezoneMutation = (
  options: UseMutationOptions<
    Account,
    unknown,
    UpdateAccountTimezonePayload
  > = {},
): UseMutationResult<Account, unknown, UpdateAccountTimezonePayload> => {
  const queryClient = useQueryClient();

  return useMutation((data) => updateAccTimezoneFn(data), {
    ...options,

    onMutate: async () => {
      await queryClient.cancelQueries(ACCOUNT_PROFILE_KEY);

      const previous = queryClient.getQueryData<Account>(ACCOUNT_PROFILE_KEY);

      return { previous };
    },

    onSuccess: async (data, request, context) => {
      const prev = context?.previous;

      if (!prev) {
        return;
      }

      queryClient.setQueryData<Account>(ACCOUNT_PROFILE_KEY, {
        ...prev,
        timeZone: data.timeZone,
      });
    },
  });
};

export const CURRENCY_LIST_KEY = ['CURRENCY_LIST'];

export const useCurrencyListQuery = (
  queryOptions: UseQueryOptions<
    CurrencyList,
    unknown,
    CurrencyList,
    typeof CURRENCY_LIST_KEY
  > = {},
): UseQueryResult<CurrencyList> =>
  useQuery(CURRENCY_LIST_KEY, () => AccountAPI.getCurrencyList(), {
    ...queryOptions,
    staleTime: Infinity,
  });

export const useCurrencyMappingListQuery = (
  queryOptions: UseQueryOptions<
    CurrencyMappingList,
    unknown,
    CurrencyMappingList,
    typeof CURRENCY_MAPPING_LIST_KEY
  > = {},
): UseQueryResult<CurrencyMappingList> =>
  useQuery(
    CURRENCY_MAPPING_LIST_KEY,
    () => AccountAPI.getCurrencyMappingList(),
    {
      ...queryOptions,
    },
  );

export const useAddCurrencyMappingMutation = (
  options: UseMutationOptions<void, unknown, CurrencyMapping> = {},
): UseMutationResult<void, unknown, CurrencyMapping> => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: CurrencyMapping) => AccountAPI.addCurrencyMapping(payload),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries(CURRENCY_MAPPING_LIST_KEY);
      },
    },
  );
};

export const useUpdateCurrencyMappingMutation = (
  options: UseMutationOptions<void, unknown, UpdateCurrencyMappingPayload> = {},
): UseMutationResult<void, unknown, UpdateCurrencyMappingPayload> => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: UpdateCurrencyMappingPayload) =>
      AccountAPI.updateCurrencyMapping(payload),
    {
      ...options,
      onSuccess: (_, payload: UpdateCurrencyMappingPayload) => {
        queryClient.invalidateQueries(CURRENCY_MAPPING_LIST_KEY);
        queryClient.invalidateQueries(
          getCurrencyMappingHistoryKey(payload.currency.currencyCode),
        );
      },
    },
  );
};

// Accept undefined currency: callers (currency settings drawer/modal) can
// pass an undefined currency during the post-mutation re-render when the
// drawer's form state momentarily resets. Destructuring `currencyCode` from
// the parameter directly throws synchronously — even with `enabled: false`
// — because the hook body always runs before React Query consults `enabled`.
export const useCurrencyMappingHistoryQuery = (
  currency: Currency | undefined,
  queryOptions: UseQueryOptions<
    CurrencyMappingList,
    unknown,
    CurrencyMappingList,
    CurrencyMappingHistoryKey
  > = {},
): UseQueryResult<CurrencyMappingList> =>
  useQuery(
    getCurrencyMappingHistoryKey(currency?.currencyCode ?? ''),
    () => AccountAPI.getCurrencyMappingHistory(currency!.currencyCode),
    {
      ...queryOptions,
      enabled: currency != null && (queryOptions.enabled ?? true),
      cacheTime: 0,
    },
  );

export const useCurrencyMappingAuditLogsQuery = (
  currency: Currency | undefined,
  queryOptions: UseQueryOptions<
    AuditLog<CurrencyAuditLogMeta>[],
    unknown,
    AuditLog<CurrencyAuditLogMeta>[],
    CurrencyMappingAuditLogKey
  > = {},
): UseQueryResult<AuditLog<CurrencyAuditLogMeta>[]> =>
  useQuery(
    getCurrencyMappingAuditLogKey(currency?.currencyCode ?? ''),
    () =>
      AccountAPI.getCurrencyMappingAuditLog(currency!.currencyCode).then(
        (data) =>
          data.map((_data) => ({
            ..._data,
            meta: {
              ..._data.meta,
              metaType: AuditLogMetaName.Currency,
            } as CurrencyAuditLogMeta,
          })),
      ),
    {
      ...queryOptions,
      enabled: currency != null && (queryOptions.enabled ?? true),
      cacheTime: 0,
    },
  );

export const useUserMetaDataMutation = (userId: UserId) =>
  useMutation((payload: Partial<UserMetaData>) =>
    UserAPI.updateMetaDataUser(userId, payload),
  );

export const useUpdateCalendarConfigMutation = () => {
  return useMutation(
    getCustomCalendarConfigKey(),
    (payload: CalendarConfigPayload) =>
      AccountAPI.updateCalendarConfig(payload),
  );
};
